import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Search,
  Filter,
  Download,
  Trash2,
  Heart,
  MoreVertical,
  Grid,
  List,
  Calendar,
  Eye,
  FileImage,
  Loader2,
  Tag,
  Edit3,
  Sparkles,
  X,
  Check,
  FolderOpen,
  Scissors
} from 'lucide-react';
import Button from '../../components/Button';
import ImagePreviewModal from '../../components/ImagePreviewModal';
import { assetService, Asset } from '../../services/assetService';
import { downloadService } from '../../services/downloadService';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthRefresh } from '../../hooks/useAuthRefresh';
import { API_BASE_URL } from '../../utils/apiUrl';

const Assets: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [removingBackground, setRemovingBackground] = useState<string | null>(null);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);

  const itemsPerPage = 12;

  // Use auth refresh hook to reload data when session refreshes
  useAuthRefresh(() => {
    if (user && !authLoading) {
      console.log('Auth refreshed, reloading assets data...');
      loadAssets();
      loadCategories();
    }
  });

  useEffect(() => {
    // Wait for auth to initialize
    if (authLoading) return;
    if (!user) {
      setAssets([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    loadAssets();
    loadCategories();
  }, [selectedCategory, searchQuery, page, user, authLoading]);

  const loadCategories = async () => {
    const cats = await assetService.getCategories();
    setCategories(cats);
  };

  const loadAssets = async () => {
    if (!user) {
      setAssets([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { assets: data, count, error } = await assetService.getUserAssets({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        searchQuery: searchQuery || undefined
      });

      if (error) {
        console.error('Error loading assets:', error);
      } else {
        setAssets(data);
        setTotalCount(count);
      }
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = async (files: FileList) => {
    const file = files[0]; // Handle one file at a time for now
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const result = await assetService.uploadAsset({
        file,
        category: selectedCategory === 'all' ? 'general' : selectedCategory
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.asset) {
        console.log('Asset uploaded successfully');
        loadAssets(); // Reload assets
        setTimeout(() => {
          setUploadProgress(0);
          setIsUploading(false);
        }, 500);
      } else {
        alert(`Failed to upload: ${result.error}`);
        setIsUploading(false);
        setUploadProgress(0);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleToggleFavorite = async (asset: Asset) => {
    const result = await assetService.toggleFavorite(asset.id);
    if (result.success) {
      loadAssets();
    }
  };

  const handleDelete = async (asset: Asset) => {
    if (!window.confirm(`Are you sure you want to delete "${asset.original_name}"?`)) {
      return;
    }

    setIsDeleting(asset.id);
    const result = await assetService.deleteAsset(asset.id);
    
    if (result.success) {
      loadAssets();
    } else {
      alert(`Failed to delete: ${result.error}`);
    }
    setIsDeleting(null);
  };

  const handleGenerateMockup = async (asset: Asset) => {
    // Increment usage count
    await assetService.incrementUsageCount(asset.id);
    
    // Store asset info in session
    sessionStorage.clear();
    sessionStorage.setItem('assetImageId', asset.id);
    sessionStorage.setItem('assetImageUrl', asset.file_url);
    sessionStorage.setItem('assetImageType', asset.file_type);
    sessionStorage.setItem('creationStep', '1');
    
    // Navigate to mockup templates
    navigate('/dashboard/mockups');
  };

  const handleRemoveBackground = async (asset: Asset) => {
    if (!asset.id || removingBackground) return;
    
    setRemovingBackground(asset.id);
    
    try {
      const result = await assetService.removeBackground(asset.id, asset.file_url);
      
      if (result.error) {
        alert(`Background removal failed: ${result.error}`);
      } else if (result.asset) {
        // Show the result in the in-app preview modal
        const resultUrl = result.asset.url.startsWith('http') ? result.asset.url : `${API_BASE_URL}${result.asset.url}`;
        setPreviewAsset({
          ...asset,
          file_url: resultUrl,
          original_name: result.asset.filename || `${asset.original_name} (no background)`,
        });
      }
    } catch (error: any) {
      console.error('Background removal error:', error);
      alert('Failed to remove background');
    } finally {
      setRemovingBackground(null);
    }
  };

  const handleDownloadAsset = async (asset: Asset) => {
    const url = asset.file_url.startsWith('http') ? asset.file_url : `${API_BASE_URL}${asset.file_url}`;

    try {
      await downloadService.downloadFileFromUrl(url, asset.original_name);
    } catch (error) {
      console.error('Asset download failed:', error);
      alert('Failed to download asset');
    }
  };

  const handleUpdateAsset = async () => {
    if (!editingAsset) return;

    const result = await assetService.updateAsset(editingAsset.id, {
      category: editingAsset.category,
      description: editingAsset.description,
      tags: editingAsset.tags
    });

    if (result.success) {
      loadAssets();
      setEditingAsset(null);
    } else {
      alert(`Failed to update: ${result.error}`);
    }
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading && assets.length === 0) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-h2 font-heading text-text-dark mb-2">
              Asset Manager
            </h1>
            <p className="text-body text-body-grey">
              Upload and manage your design assets
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-body-grey">
            <span>{totalCount} assets total</span>
            <span>•</span>
            <span>{assets.filter(a => a.is_favorite).length} favorites</span>
          </div>
        </div>

        {/* Upload Area */}
        <div
          className={`mb-6 border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragActive ? 'border-brand-purple bg-brand-purple/5' : 'border-gray-300 hover:border-gray-400'
          } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <div className="space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-brand-purple mx-auto" />
              <p className="text-gray-600">Uploading asset...</p>
              <div className="w-full max-w-xs mx-auto bg-gray-200 rounded-full h-2">
                <div
                  className="bg-brand-purple h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Drag and drop your files here, or click to browse
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Supports JPG, PNG, SVG, WebP (Max 10MB)
              </p>
              <Button
                variant="secondary"
                size="medium"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose Files
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'} transition-colors`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'} transition-colors border-l border-gray-300`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Assets Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {assets.map((asset) => (
            <div key={asset.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all group">
              {/* Thumbnail */}
              <div className="aspect-square relative overflow-hidden rounded-t-xl bg-gray-100">
                <img 
                  src={asset.thumbnail_url || asset.file_url} 
                  alt={asset.original_name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewAsset(asset)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Preview"
                    >
                      <Eye className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleGenerateMockup(asset)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Generate Mockup"
                    >
                      <Sparkles className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleToggleFavorite(asset)}
                      className={`p-2 bg-white rounded-full hover:bg-gray-100 transition-colors ${
                        asset.is_favorite ? 'text-red-500' : 'text-gray-700'
                      }`}
                      title="Toggle Favorite"
                    >
                      <Heart className={`w-5 h-5 ${asset.is_favorite ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleRemoveBackground(asset)}
                      disabled={removingBackground === asset.id}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remove Background"
                    >
                      {removingBackground === asset.id ? (
                        <Loader2 className="w-5 h-5 text-gray-700 animate-spin" />
                      ) : (
                        <Scissors className="w-5 h-5 text-gray-700" />
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedAsset(asset)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="More Options"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-700" />
                    </button>
                  </div>
                </div>
                {asset.category !== 'general' && (
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 bg-white/90 backdrop-blur-sm text-xs rounded-lg capitalize">
                      {asset.category}
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-text-dark truncate mb-1">{asset.original_name}</h3>
                {asset.description && (
                  <p className="text-sm text-body-grey mb-2 line-clamp-2">{asset.description}</p>
                )}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{formatFileSize(asset.file_size)}</span>
                  {asset.width && asset.height && (
                    <span>{asset.width} × {asset.height}</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                  <span className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {format(new Date(asset.created_at), 'MMM d, yyyy')}
                  </span>
                  <span>Used {asset.usage_count} times</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asset
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dimensions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {assets.map((asset) => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img 
                        src={asset.thumbnail_url || asset.file_url} 
                        alt={asset.original_name}
                        className="w-10 h-10 rounded object-cover"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {asset.original_name}
                          {asset.is_favorite && (
                            <Heart className="w-4 h-4 text-red-500 fill-current ml-2" />
                          )}
                        </div>
                        {asset.description && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {asset.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">{asset.category}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{formatFileSize(asset.file_size)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {asset.width && asset.height ? `${asset.width} × ${asset.height}` : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{asset.usage_count}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {format(new Date(asset.created_at), 'MMM d, yyyy')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setPreviewAsset(asset)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleGenerateMockup(asset)}
                        className="text-brand-purple hover:text-brand-purple/80"
                        title="Generate Mockup"
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleFavorite(asset)}
                        className={`${asset.is_favorite ? 'text-red-500' : 'text-gray-400'} hover:text-red-600`}
                        title="Toggle Favorite"
                      >
                        <Heart className={`w-4 h-4 ${asset.is_favorite ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleRemoveBackground(asset)}
                        disabled={removingBackground === asset.id}
                        className="text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Remove Background"
                      >
                        {removingBackground === asset.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Scissors className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => setEditingAsset(asset)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(asset)}
                        className="text-red-600 hover:text-red-800"
                        disabled={isDeleting === asset.id}
                        title="Delete"
                      >
                        {isDeleting === asset.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {assets.length === 0 && !loading && (
        <div className="text-center py-12">
          <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No assets found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery ? 'Try adjusting your search terms' : 'Upload your first asset to get started'}
          </p>
          {!searchQuery && (
            <Button
              variant="primary"
              size="medium"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Asset
            </Button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {/* More Options Modal */}
      {selectedAsset && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedAsset(null)}
        >
          <div 
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Asset Options</h3>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setPreviewAsset(selectedAsset);
                  setSelectedAsset(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Eye className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Preview</span>
              </button>
              <button
                onClick={() => {
                  handleGenerateMockup(selectedAsset);
                  setSelectedAsset(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Sparkles className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Generate Mockup</span>
              </button>
              <button
                onClick={() => {
                  handleDownloadAsset(selectedAsset);
                  setSelectedAsset(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Download className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Download Original</span>
              </button>
              <button
                onClick={() => {
                  setEditingAsset(selectedAsset);
                  setSelectedAsset(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Edit3 className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Edit Details</span>
              </button>
              <button
                onClick={() => {
                  handleDelete(selectedAsset);
                  setSelectedAsset(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 rounded-lg transition-colors text-red-600"
              >
                <Trash2 className="w-5 h-5" />
                <span>Delete Asset</span>
              </button>
            </div>
            <button
              onClick={() => setSelectedAsset(null)}
              className="mt-4 w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingAsset && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setEditingAsset(null)}
        >
          <div 
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Asset Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={editingAsset.category}
                  onChange={(e) => setEditingAsset({ ...editingAsset, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editingAsset.description || ''}
                  onChange={(e) => setEditingAsset({ ...editingAsset, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
                  rows={3}
                  placeholder="Add a description..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={editingAsset.tags?.join(', ') || ''}
                  onChange={(e) => setEditingAsset({ 
                    ...editingAsset, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
                  placeholder="logo, branding, icon (comma separated)"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleUpdateAsset}
                className="flex-1 px-4 py-2 bg-brand-purple text-white rounded-lg hover:bg-brand-purple/90"
              >
                Save Changes
              </button>
              <button
                onClick={() => setEditingAsset(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewAsset && (
        <ImagePreviewModal
          src={previewAsset.file_url.startsWith('http') ? previewAsset.file_url : `${API_BASE_URL}${previewAsset.file_url}`}
          alt={previewAsset.original_name}
          title={previewAsset.original_name}
          subtitle={[
            previewAsset.width && previewAsset.height ? `${previewAsset.width} x ${previewAsset.height}` : null,
            formatFileSize(previewAsset.file_size),
          ].filter(Boolean).join(' \u00B7 ')}
          onClose={() => setPreviewAsset(null)}
          onDownload={() => handleDownloadAsset(previewAsset)}
        />
      )}
    </div>
  );
};

export default Assets;
